import type { FC } from 'react';
import { Divider, Flex, Text } from '@chakra-ui/react';
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import { FormattedMessage } from 'react-intl';
import { Modal } from '../../ui-components/modal';
import {
    BrandLogo,
    CircleArrowNorthEastIcon,
    PlusIcon,
    SafoniaBranchIcon,
    SafoniaColoredIcon,
    SafoniaHeadsetIcon,
    SafoniaShippingIcon,
} from '../icons';
import { CustomLogoBox } from './CustomLogoBox';

const linkStyles = {
    textStyle: 'protection-coverage-text',
    cursor: 'pointer',
    sx: {
        textDecorationLine: 'underline',
        textDecorationStyle: 'solid',
        textDecorationSkipInk: 'none',
        textDecorationThickness: 'auto',
        textUnderlineOffset: 'auto',
        textUnderlinePosition: 'from-font',
        textAlign: 'center',
        justifyContent: 'center',
    },
};

interface ShippingProtectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    siteUrl: string;
}

const CustomTitle: FC = () => (
    <Flex align="center" gap={2}>
        <SafoniaColoredIcon
            data-testid="safonia-colored-icon"
            width={{ base: '120px' }}
            height={{ base: '24px' }}
        />
        <PlusIcon data-testid="plus-icon" />
        <BrandLogo
            data-testid="header.logo.image"
            aria-label="Brand Logo"
            width={{ base: '106px' }}
            height={{ base: '24px' }}
        />
    </Flex>
);

// TODO: Add translations for the modal content without the app crashing.

export const ShippingProtectionModal: FC<ShippingProtectionModalProps> = ({
    isOpen,
    onClose,
    siteUrl,
}) => (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        id="shipping-protection-modal"
        modalOnly
        size={{
            base: 'xl',
            sm: '2xl',
            md: '4xl',
            lg: '5xl',
        }}
        containerStyle={{
            padding: 0,
        }}
    >
        <Flex direction="row" width="100%">
            <Flex
                direction="column"
                gap={4}
                width={{ sm: '100%', md: '60%' }}
                padding={{ xs: 4, sm: 4, md: 8 }}
            >
                <CustomTitle />
                <Text textStyle="protection-coverage-text" fontWeight={400} textAlign="left">
                    <FormattedMessage
                        defaultMessage="We provide shipping protection, so you can feel confident knowing your order is safeguarded against theft, loss, or damage."
                        id="shippingProtection.guarantee"
                    />
                </Text>
                <Flex direction="column" gap={3} py={8}>
                    <Flex gap={3}>
                        <CustomLogoBox>
                            <SafoniaShippingIcon />
                        </CustomLogoBox>

                        <Flex direction="column" flex={1}>
                            <Text textStyle="protection-coverage-subtitle">
                                <FormattedMessage
                                    defaultMessage="Shipping Protection"
                                    id="shippingProtection.shippingProtection"
                                />
                            </Text>
                            <Text textStyle="protection-coverage-text">
                                <FormattedMessage
                                    defaultMessage="From theft, loss, damage, & more - Safonia has you covered."
                                    id="shippingProtection.coverage"
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    <Flex gap={3}>
                        <CustomLogoBox>
                            <SafoniaBranchIcon />
                        </CustomLogoBox>
                        <Flex direction="column" flex={1}>
                            <Text textStyle="protection-coverage-subtitle">
                                <FormattedMessage
                                    defaultMessage="Boost your Perks"
                                    id="shippingProtection.boostPerks"
                                />
                            </Text>
                            <Text textStyle="protection-coverage-text">
                                <FormattedMessage
                                    defaultMessage="Get 25% more rewards on every purchase!"
                                    id="shippingProtection.moreRewards"
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    <Flex gap={3}>
                        <CustomLogoBox>
                            <SafoniaHeadsetIcon />
                        </CustomLogoBox>
                        <Flex direction="column" flex={1}>
                            <Text textStyle="protection-coverage-subtitle">
                                <FormattedMessage
                                    defaultMessage="Priority Support"
                                    id="shippingProtection.prioritySupport"
                                />
                            </Text>
                            <Text textStyle="protection-coverage-text">
                                <FormattedMessage
                                    defaultMessage="Skip the line and get our Premium Support!"
                                    id="shippingProtection.premiumSupport"
                                />
                            </Text>
                        </Flex>
                    </Flex>
                </Flex>
                <Flex direction={{ base: 'column', sm: 'row' }} gap={1}>
                    <Text textStyle="protection-coverage-text" textAlign="left">
                        <FormattedMessage
                            defaultMessage="*Perks will be calculated over a 3-month period."
                            id="shippingProtection.perksCalculation"
                        />
                    </Text>
                    <Flex
                        cursor="pointer"
                        as="a"
                        href={siteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        align="center"
                        gap={1}
                    >
                        <Text
                            textStyle="protection-coverage-text"
                            sx={{
                                textDecorationLine: 'underline',
                                textDecorationStyle: 'solid',
                                textDecorationSkipInk: 'none',
                                textDecorationThickness: 'auto',
                                textUnderlineOffset: 'auto',
                                textUnderlinePosition: 'from-font',
                            }}
                        >
                            <FormattedMessage
                                defaultMessage="Learn More"
                                id="shippingProtection.learnMore"
                            />
                        </Text>
                        <CircleArrowNorthEastIcon />
                    </Flex>
                </Flex>

                <Divider borderColor="slt-black.200" marginTop={6} marginBottom={0} />

                <Flex direction="row" pt={4} gap={{ base: 3, sm: 6 }} justifyContent="center">
                    <Text
                        {...linkStyles}
                        as="a"
                        href="https://safonia.ai/faq"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FormattedMessage defaultMessage="FAQ" id="shippingProtection.faq" />
                    </Text>
                    <Text
                        {...linkStyles}
                        as="a"
                        href="https://safonia.ai/terms-and-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FormattedMessage
                            defaultMessage="Terms and Conditions"
                            id="shippingProtection.termsConditions"
                        />
                    </Text>
                    <Text
                        {...linkStyles}
                        as="a"
                        href="https://safonia.ai/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FormattedMessage
                            defaultMessage="Privacy Policy"
                            id="shippingProtection.privacyPolicy"
                        />
                    </Text>
                </Flex>
            </Flex>
            <Flex
                flex={{ base: 0, xs: 0, md: 1 }}
                backgroundImage={`url(${getAssetUrl('static/img/safonia/safonia-background.webp')})`}
                backgroundSize="cover"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                data-testid="safonia-background-image"
            />
        </Flex>
    </Modal>
);
